/* eslint-disable ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
// Extends From
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';

// Internal Imports
import EventStatus from '@linc-technologies/linced-common/enums/events/event-status';
import {
  byKeys,
  mapBy,
  resolved,
} from '@linc-technologies/linced-common/utils/arrays';
import { getGroupVisibilityFilter } from 'linced-app/utils/groups';

// Ember Imports
import { action, computed } from '@ember/object';
import { setDiff } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';

// External Imports
import dayGridPlugin from 'fullcalendar/daygrid';
import interactionPlugin from 'fullcalendar/interaction';
import listPlugin from 'fullcalendar/list';
import timeGridPlugin from 'fullcalendar/timegrid';

const locale = 'en-NZ';

const FullCalendarEventTimeFormat = {
  hour: 'numeric',
  minute: '2-digit',
  omitZeroMinute: true,
  hour12: true,
  meridiem: 'short',
};
const FullCalendarInitialView = 'dayGridMonth';
const FullCalendarHeaderToolbar = {
  left: 'title listYear,timeGridWeek,dayGridMonth',
  right: 'today prev,next',
};
const FullCalendarPlugins = [
  dayGridPlugin,
  interactionPlugin,
  listPlugin,
  timeGridPlugin,
];
const FullCalendarViews = {
  listYear: {
    type: 'listWeek',
    duration: { weeks: 52 },
    buttonText: 'List',
    listDayFormat: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
  },
};

export default class IndexController extends Controller {
  // Constants
  queryParams = ['authorIds', 'groupIds'];

  FullCalendarEventTimeFormat = FullCalendarEventTimeFormat;
  FullCalendarInitialView = FullCalendarInitialView;
  FullCalendarHeaderToolbar = FullCalendarHeaderToolbar;
  FullCalendarPlugins = FullCalendarPlugins;
  FullCalendarViews = FullCalendarViews;
  locale = locale;

  // Services
  @service
  router;

  @service
  session;

  @service
  store;

  @service
  toaster;

  // State
  @tracked
  authorIds = [];

  @tracked
  groupIds = [];
  @tracked newEventDialogOpen = false;
  @tracked eventFilter = '';
  @tracked
  statusFilters = [];
  @tracked selectedDate = null;

  @computed('authorIds.[]')
  get selectedAuthors() {
    return this.authorIds.map((id) => {
      return this.store.peekRecord('person', id);
    });
  }

  @setDiff('model.staff', 'selectedAuthors')
  remainingAuthors;

  @computed('remainingAuthors')
  get sortedRemainingAuthors() {
    return this.remainingAuthors
      .slice()
      .sort(
        (a, b) =>
          (a.lastName || '').localeCompare(b.lastName || '') ||
          (a.firstName || '').localeCompare(b.firstName || ''),
      );
  }

  @computed('groupIds.[],model.groups.[]')
  get selectedGroups() {
    return this.groupIds.map((id) => {
      return this.store.peekRecord('group', id);
    });
  }

  @setDiff('model.groups', 'selectedGroups')
  remainingGroups;

  @computed('remainingGroups.[]', 'session.personId')
  get sortedFilteredRemainingGroups() {
    return this.remainingGroups
      .filter(getGroupVisibilityFilter(this.session.personId))
      .sort(byKeys('name'));
  }

  // Functions
  @action
  addAuthor(author) {
    if (this.authorIds.length >= 5) {
      this.toaster.error(
        'Filtering has a maximum of 5 authors. Please remove one before adding more.',
      );
    } else {
      this.authorIds = [...this.authorIds, author.get('id')];
    }
  }

  @action
  removeAuthor(author) {
    let id = author.get('id');
    this.authorIds = this.authorIds.filter((authorId) => authorId !== id);
  }

  @action
  addGroup(group) {
    if (this.groupIds.length >= 5) {
      this.toaster.error(
        'Filtering has a maximum of 5 groups. Please remove one before adding more.',
      );
    } else {
      this.groupIds = [...this.groupIds, group.id];
    }
  }

  @action
  removeGroup(group) {
    this.groupIds = this.groupIds.filter((id) => id !== group.id);
  }

  // groupsLoader forces filteredEvents to re-run once each event's groups
  // relation (needed below for the 'my' filter matching group-staffing) has
  // resolved. event.groups is an async hasMany, and resolved() reads it
  // as [] before it settles - since none of filteredEvents' other dependent
  // keys change once an outstanding relation like that resolves, an event
  // where the current user is only linked through a group they staff would
  // otherwise stay permanently excluded.
  @tracked groupsLoadToken = 0;

  @computed('model.events.[]')
  get groupsLoader() {
    this.model.events.forEach((event) => {
      event.groups?.then(() => {
        this.groupsLoadToken++;
      });
    });
    return this.model.events.length;
  }

  @computed(
    'model.events.[]',
    'eventFilter',
    'statusFilters.[]',
    'session.personId',
    'groupsLoader',
    'groupsLoadToken',
  )
  get filteredEvents() {
    return this.model.events.filter((event) => {
      let eventFilterMatch = (() => {
        switch (this.eventFilter) {
          case '':
            return true;
          case 'my': {
            let currentPersonId = this.session.personId;
            if (event.author.get('id') === currentPersonId) {
              return true;
            }

            if (event.staff_ids.includes(currentPersonId)) {
              return true;
            }

            // groups is an async hasMany (a PromiseManyArray): array-mixin
            // methods on it trigger the "Do not use A()" deprecation (see
            // events/event.js's group_ids), so resolve to its plain
            // content first, matching that model's established pattern.
            return resolved(event.groups).some((group) => {
              if (isEmpty(group)) {
                return false;
              }

              let staffPeople = group.currentStaffPeople;
              if (isEmpty(staffPeople)) {
                return false;
              }

              return staffPeople.some((staffPerson) => {
                if (isEmpty(staffPerson)) {
                  return false;
                }
                return staffPerson.get('id') === currentPersonId;
              });
            });
          }
          case 'internal':
            return event.internal;
          case 'external':
            return !event.internal;
          default:
            return false;
        }
      })();

      let statusFilterMatch = this.statusFilters.some((filter) => {
        switch (filter) {
          case 'draft':
            return event.status === EventStatus.DRAFT;
          case 'pending':
            return event.status === EventStatus.PENDING_APPROVAL;
          case 'approved':
            return event.status === EventStatus.APPROVED;
          case 'public':
            return (
              event.status === EventStatus.OPEN_TO_CAREGIVERS ||
              event.status === EventStatus.OPEN_TO_STAFF
            );
          case 'complete':
            return event.status === EventStatus.COMPLETE;
          case 'cancelled':
            return event.status === EventStatus.CANCELLED;
          case 'closed':
            return event.status === EventStatus.CLOSED;
          default:
            return true;
        }
      });

      if (this.statusFilters.length) {
        return eventFilterMatch && statusFilterMatch;
      }

      return eventFilterMatch;
    });
  }

  // Munge events into a format suitable for Full Calendar
  @computed('filteredEvents.[]')
  get fullCalendarEvents() {
    return mapBy(this.filteredEvents, 'fullCalendarEventModel');
  }

  eventDidMount(info) {
    // Add a title attribute for a basic tooltip
    info.el.title = info.event.title;
  }

  @action
  refresh() {
    this.model.update();
  }

  @action
  eventClick(event) {
    this.router.transitionTo('home.events.event', event.event.id);
  }

  @action
  dateClick(date) {
    let selectedDate = date.dateStr;
    this.selectedDate = selectedDate;
    this.newEventDialogOpen = true;
  }

  @action
  addStatusFilter(filter) {
    if (this.statusFilters.includes(filter)) {
      this.statusFilters = this.statusFilters.filter((f) => f !== filter);
    } else {
      this.statusFilters = [...this.statusFilters, filter];
    }
  }

  @action
  setEventFilter(filter) {
    if (this.eventFilter === filter) {
      this.eventFilter = '';
    } else {
      this.eventFilter = filter;
    }
  }
}
